import * as remoteData from "../RemoteData";
import { useQuery } from "../useAPI";
import * as uKontoApi from "../UKontoFirstPart/UKontoPackagesSelection/api";
import * as cfApi from "../StandardLoan/api";
import { useIsInPersonChannel } from "../useChannel";
import { pipe } from "fp-ts/function";
import { array, boolean, nonEmptyArray, option, taskEither } from "fp-ts";
import {
  GetListError,
  PackageListResponse,
  PackageType,
  UKontoPackage,
} from "./api";
import {
  Box,
  Carousel,
  ErrorBanner,
  FeedbackBlock,
  FeedbackDialog,
  Heading,
  Space,
  Stack,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { LocaleKey, useFormatMessage, useFormatRichMessage2 } from "../intl";
import * as classes from "./PackagesSelection.css";
import cx from "classnames";
import { useEffect, useState } from "react";
import { useAppContext } from "../useAppContext";
import { reducerConfig, setPackageAction, setSharedAction } from "./state";
import { useParentSharedReducer } from "../BranchExperience/useSharedReducer";
import { BackButton } from "../Common/BackButton/BackButton";
import { IO } from "fp-ts/IO";
import { sequenceS } from "fp-ts/Apply";
import { useScrollTopOnChange } from "../Common/useScrollTopOnChange";
import { PackageCard } from "./PackageCard";

type PackagesSelectionCommonProps = {
  chosenPackage: option.Option<PackageType>;
  onPackageConfirm: option.Option<
    (packageType: PackageType) => taskEither.TaskEither<void, void>
  >;
  child: boolean;
  isCf?: boolean;
  onBack?: IO<unknown>;
  showMastercard: boolean;
  packageList: PackageListResponse;
};

type PackagesSelectionProps = Omit<
  PackagesSelectionCommonProps,
  "showMastercard" | "packageList"
> &
  (
    | {
        child: true;
        showMastercard: boolean;
        packageList: PackageListResponse;
      }
    | { child: false }
  );

export const PackagesSelection = (props: PackagesSelectionProps) => {
  return props.child ? (
    <PackagesSelectionCommon {...props} />
  ) : (
    <PackagesSelectionParent {...props} />
  );
};

const PackagesSelectionParent = (props: PackagesSelectionProps) => {
  const {
    config: { enableMastercardImpl },
  } = useAppContext();
  const formatMessage = useFormatMessage();
  useScrollTopOnChange("smooth");

  const [mastercardWhitelist] = useQuery(uKontoApi.getFeatureWhitelist, {
    feature: "enableMastercardImpl",
  });
  const [packages] = useQuery(
    props.isCf ? cfApi.packagesList : uKontoApi.packagesList
  );

  return pipe(
    { mastercardWhitelist, packages },
    sequenceS(remoteData.remoteData),
    remoteData.fold(
      () =>
        props.isCf ? null : (
          <FeedbackBlock
            type="loading"
            size="large"
            heading={formatMessage("Loading")}
            subheading={option.none}
          />
        ),
      response => {
        if (GetListError.is(response) && "code" in response) {
          return (
            <ErrorBanner>
              {formatMessage(
                ("AccountCollectionByClient." + response.code) as LocaleKey
              )}
            </ErrorBanner>
          );
        } else {
          return <ErrorBanner>{formatMessage("GenericError")}</ErrorBanner>;
        }
      },
      ({ mastercardWhitelist, packages }) => (
        <PackagesSelectionCommon
          {...props}
          showMastercard={mastercardWhitelist || enableMastercardImpl}
          packageList={packages}
        />
      )
    )
  );
};

const PackagesSelectionCommon = (props: PackagesSelectionCommonProps) => {
  const [selectedCarouselIndex, setSelectedCarouselIndex] = useState(1);
  const [selectedPackageType, setSelectedPackageType] = useState<
    option.Option<PackageType>
  >(props.chosenPackage);
  const formatMessage = useFormatMessage();
  const formatRichMessage = useFormatRichMessage2();
  const isMobileLayout = useIsMobileLayout();
  const [
    visibleDialogReservedAccountNumber,
    setVisibleDialogReservedAccountNumber,
  ] = useState(true);
  const [state, dispatch] = useParentSharedReducer(reducerConfig, {
    id: "Initial",
    package: selectedPackageType,
    sharedWithClient: false,
    showMastercard: props.showMastercard,
    packageList: props.packageList,
  });
  const isInPerson = useIsInPersonChannel();
  const {
    config: { cashLoanStartPackageOnly },
  } = useAppContext();

  const onlyStartCF = props.isCf && cashLoanStartPackageOnly;

  useEffect(() => {
    const timer = setTimeout(() => {
      const activeSlide = document.querySelector(
        ".slick-slide.slick-active.slick-current"
      );
      if (activeSlide) {
        if (activeSlide.hasAttribute("tabindex")) {
          activeSlide.removeAttribute("tabindex");
        }
        const firstChild = activeSlide.firstElementChild as HTMLElement | null;
        if (firstChild) {
          const secondChild = firstChild.firstElementChild as HTMLElement | null;
          if (secondChild?.hasAttribute("tabindex")) {
            secondChild.removeAttribute("tabindex");
          }
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedCarouselIndex]);

  const cards = (
    packagesData: nonEmptyArray.NonEmptyArray<UKontoPackage>,
    showMastercard: boolean
  ) =>
    pipe(
      packagesData,
      nonEmptyArray.map(data => (
        <PackageCard
          data={data}
          variant={"default"}
          onConfirm={props.onPackageConfirm}
          onChoose={p => dispatch(setPackageAction(p))}
          onSharing={p => dispatch(setSharedAction(p))}
          currentState={
            props.isCf
              ? {
                  id: "Initial",
                  package: selectedPackageType,
                  sharedWithClient: false,
                  showMastercard: showMastercard,
                  packageList: props.packageList,
                }
              : state
          }
          isCf={props.isCf}
          showMastercard={showMastercard}
        />
      ))
    );

  const getPackageName = (index: number) => {
    switch (index) {
      case 2:
        return formatMessage("packages.middle.name");
      case 3:
        return formatMessage("packages.upper.name");
      default:
        return formatMessage(
          onlyStartCF ? "CF.packages.basic.name" : "packages.basic.name"
        );
    }
  };

  return pipe(props.packageList, packagesData => {
    if (
      option.isNone(selectedPackageType) &&
      props.isCf &&
      packagesData.selectedPackageType !== selectedPackageType
    ) {
      setSelectedPackageType(packagesData.selectedPackageType);
    }
    let account = pipe(
      packagesData.reservedAccountNumber,
      option.fold(
        () => {
          return "";
        },
        accountNo => {
          return accountNo;
        }
      )
    );
    return (
      <Box column grow className={cx(classes.container)}>
        {isInPerson &&
          visibleDialogReservedAccountNumber &&
          !props.child &&
          "" !== account && (
            <FeedbackDialog
              ariaLabelMessageForCloseIcon={formatMessage("Modal.closeButton")}
              type="warning"
              title={formatMessage(
                "UkontoFirstPart.ReservedAccountNumber.title",
                {
                  account: account,
                }
              )}
              cta={{
                label: formatMessage(
                  "UkontoFirstPart.ReservedAccountNumber.continue"
                ),
                action: () => setVisibleDialogReservedAccountNumber(false),
              }}
            />
          )}
        <Stack
          column
          units={2}
          className={
            isMobileLayout
              ? classes.mobileTitleContainer
              : classes.titleContainer
          }
          style={{ justifyContent: "space-between" }}
        >
          <Box>
            <Heading
              size="medium"
              weight="medium"
              ariaLabel={formatMessage(
                "UKontoFirstPart.UKontoPackagesSelection.title.ariaLabel"
              )}
            >
              {props.isCf
                ? onlyStartCF
                  ? formatMessage("CF.PackagesSelection.title")
                  : formatMessage(
                      "UKontoFirstPart.UKontoPackagesSelection.subtitle"
                    )
                : formatMessage(
                    "UKontoFirstPart.UKontoPackagesSelection.title"
                  )}
            </Heading>
          </Box>
          {!isMobileLayout && (!props.isCf || onlyStartCF) && (
            <Heading size="x-small" weight="regular">
              {onlyStartCF
                ? formatMessage("CF.PackagesSelection.subtitle")
                : formatMessage(
                    "UKontoFirstPart.UKontoPackagesSelection.subtitle"
                  )}
            </Heading>
          )}
        </Stack>
        <Space units={isMobileLayout ? 4 : 12} />

        {pipe(
          isMobileLayout,
          boolean.fold(
            () => (
              <Stack
                grow
                hAlignContent="center"
                units={8}
                style={{
                  justifyContent:
                    packagesData.packagesList.length > 1
                      ? "space-between"
                      : "center",
                }}
              >
                {cards(packagesData.packagesList, props.showMastercard)}
              </Stack>
            ),
            () => (
              <Carousel
                slides={pipe(
                  cards(packagesData.packagesList, props.showMastercard),
                  array.mapWithIndex((index, card) => (
                    <Box
                      shrink
                      className={classes.cardContainer}
                      hAlignContent="center"
                      key={index}
                    >
                      {card}
                    </Box>
                  ))
                )}
                getAriaLabelForCarouselBullet={index => {
                  const packageName = getPackageName(index);
                  return formatMessage(
                    "PackagesSelection.carousel.slideBullet.altMessage",
                    { packageName }
                  );
                }}
                getAriaLabelForSlide={index => {
                  const packageName = getPackageName(index);
                  const total = props.packageList.packagesList.length;
                  return formatMessage(
                    "PackagesSelection.carousel.slide.altMessage",
                    { packageName, index, total }
                  );
                }}
                onChange={index => {
                  if (!Number.isNaN(index)) {
                    setSelectedCarouselIndex(index);
                  }
                }}
                value={selectedCarouselIndex}
                carouselAriaLabel={formatMessage(
                  "PackagesSelection.carousel.altMessage"
                )}
              />
            )
          )
        )}

        {onlyStartCF &&
          [1, 2, 3].map(i => (
            <div
              style={{
                paddingTop: "14px",
                display: "flex",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  maxWidth: isMobileLayout ? "330px" : "50%",
                }}
              >
                {formatRichMessage(
                  ("CF.PackagesSelection.description" + i) as LocaleKey
                )}
              </div>
              &nbsp;
            </div>
          ))}

        {props.isCf && (
          <Box column>
            <Space units={4} />
            <Box
              grow
              column={isMobileLayout}
              style={{
                paddingLeft: isMobileLayout ? 24 : 0,
                paddingRight: isMobileLayout ? 24 : 0,
              }}
            >
              <BackButton action={() => props.onBack && props.onBack()} />
            </Box>
          </Box>
        )}
      </Box>
    );
  });
};
